import React, { useState, useEffect, useRef } from "react";
import { FaCheck } from "react-icons/fa";
import { useInView } from "react-intersection-observer";
import Lottie from "lottie-react";

// Import animations
import coding from "../../animations/Coding.json";
import debug from "../../animations/Debug.json";
import ideation from "../../animations/Ideation.json";
import meeting from "../../animations/Meeting.json";
import scalling from "../../animations/Scalling.json";

const milestoneContent = {
  1: {
    animation: meeting,
    title: "Meeting",
    content:
      "We begin by understanding your vision, goals, and project requirements through collaborative discussions.",
  },
  2: {
    animation: ideation,
    title: "Ideation",
    content:
      "Brainstorming and concept creation take place to lay down the foundation of unique and impactful ideas.",
  },
  3: {
    animation: coding,
    title: "Coding",
    content:
      "Our developers turn concepts into reality with clean, efficient, and scalable code.",
  },
  4: {
    animation: debug,
    title: "Debugging",
    content:
      "Thorough testing and debugging ensure everything works seamlessly without errors.",
  },
  5: {
    animation: scalling,
    title: "Scaling",
    content:
      "We fine-tune and optimize the project, making it scalable and ready for real-world growth.",
  },
};

const HowItWorks = ({
  initialMilestone = 1,
  autoProgressSpeed = 5000,
  milestoneLabels = ["1", "2", "3", "4", "5"],
  onMilestoneChange,
}) => {
  const [currentMilestone, setCurrentMilestone] = useState(initialMilestone);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [ref, inView] = useInView({
    threshold: 0.5,
    triggerOnce: false,
  });

  const autoProgressInterval = useRef(null);

  useEffect(() => {
    if (inView && isAutoPlaying) {
      autoProgressInterval.current = setInterval(() => {
        setCurrentMilestone((prev) => (prev < 5 ? prev + 1 : 1));
      }, autoProgressSpeed);
    }
    return () => clearInterval(autoProgressInterval.current);
  }, [inView, isAutoPlaying, autoProgressSpeed]);

  useEffect(() => {
    if (onMilestoneChange) {
      onMilestoneChange(currentMilestone);
    }
  }, [currentMilestone, onMilestoneChange]);

  const handleMilestoneClick = (milestone) => {
    if (milestone <= currentMilestone + 1) {
      setCurrentMilestone(milestone);
      setIsAutoPlaying(false);
      clearInterval(autoProgressInterval.current);
    }
  };

  const getProgressWidth = () => {
    return (
      ((currentMilestone - 1) / (Object.keys(milestoneContent).length - 1)) *
      100
    );
  };

  return (
    <div ref={ref} className="">
      <div className="mb-8 md:flex md:gap-8 md:items-center">
        <div className="md:w-1/2">
          <Lottie
            animationData={milestoneContent[currentMilestone].animation}
            loop={true}
            autoplay={true}
            className="w-full h-[400px] lg:h-[500px]"
          />
        </div>
        <div className="md:w-1/2">
          <p className="font-semibold text-2xl lg:text-3xl transition-all duration-200 ease-in-out">
            {milestoneContent[currentMilestone].title}
          </p>
          <p className="text-sm mt-5" style={{ lineHeight: 1.8 }}>
            {milestoneContent[currentMilestone].content}
          </p>
        </div>
      </div>

      <div className="relative mb-8">
        <div className="h-2 bg-gray-300 rounded-full">
          <div
            className="h-full bg-[#008080] rounded-full transition-all duration-500 ease-in-out"
            style={{ width: `${getProgressWidth()}%` }}
          />
        </div>

        <div className="absolute top-1 left-0 w-full flex justify-between transform -translate-y-1/2">
          {[1, 2, 3, 4, 5].map((milestone) => (
            <button
              key={milestone}
              onClick={() => handleMilestoneClick(milestone)}
              className={`w-8 h-8 rounded-full flex items-center justify-center border-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#008080] transition-all duration-300 ${
                milestone <= currentMilestone
                  ? "bg-[#008080] border-[#008080] text-white"
                  : "bg-gray-300 border-gray-300 text-gray-800"
              }`}
              disabled={milestone > currentMilestone + 1}
              aria-label={`Milestone ${milestone}: ${
                milestoneLabels[milestone - 1]
              }`}
            >
              {milestone <= currentMilestone ? (
                <FaCheck className="w-4 h-4" />
              ) : (
                milestone
              )}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
